import React, { useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import {
  TrendingUp,
  Users,
  Calendar,
  Clock,
  Target,
  Eye,
  ArrowRight,
  Settings,
  Building2,
  UserPlus,
  ClipboardList,
  SlidersHorizontal,
} from "lucide-react";
import {
  Box,
  Typography,
  Skeleton,
  Tooltip,
  IconButton,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import { CheckCircleOutline, History } from "@mui/icons-material";
import SuccessPopup from "../../components/SuccessPopup";
import { UnifiedModal } from "../../components/UnifiedModal";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/Card";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Progress } from "../../components/Progress";
import { ArchivedCount, MetricCard } from "../../components/MetricCard";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import { EmptyStateContainer } from "../../components/EmptyStateContainer";
import { TaskDetailModal } from "./components/MyTasksPanel";
import { MyTasksPanel } from "./components/MyTasksPanel";
import { AnnouncementsPanel } from "./components/AnnouncementsPanel";
import { NotificationPanel } from "./components/NotificationPanel";
import { GlobalMessageWall } from "../Conversations/GlobalMessageWall";
import { IncidentFiltersModal } from "../../components/IncidentFiltersModal";
import { useTheme } from "../../theme/useTheme";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import { incidentColors } from "../../theme/theme";
import apiClient from "../../api-client";
import { encodeUrlSafeBase64 } from "../Conversations/threadUrlUtils";
import {
  getContentNotificationRoute,
  getNotificationRoute as getNotificationRouteForRole,
  getNotificationSummary,
  invalidateNotificationQueries as invalidateNotificationQueriesFor,
} from "./notificationRoutes";
import { isSupportTicketNotification, openSupportTicket } from "../../utils/supportTickets";
import { useQueryClient } from "@tanstack/react-query";
import { FilterSwitchGroup } from "../../components/FilterSwitchGroup";
import { TicketFreelancerNotifications } from "./TicketFreelancerNotifications";
import { getSetupUnavailableMessage, isExternalAgency } from "@/utils/agencyFeatures";

const CLIENT_NO_SETUP_PROJECT_MESSAGE = "client does not have a setup project";

const getApiErrorMessage = (error) => {
  if (!error) return "";
  const responseData = error?.response?.data;
  const extractString = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    try {
      return JSON.stringify(value);
    } catch (e) {
      return "";
    }
  };

  if (responseData) {
    const detail = responseData?.detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object") {
      const detailString = extractString(detail);
      if (detailString) return detailString;
    }
    const message = responseData?.message;
    if (typeof message === "string") return message;
    const messageString = extractString(message);
    if (messageString) return messageString;
    const responseString = extractString(responseData);
    if (responseString) return responseString;
  }

  if (typeof error?.message === "string") return error.message;
  return "";
};

// Filter configuration: only read/unread filters, matching Incidents styling
const NOTIFICATION_STATUS_FILTERS = {
  unread: { label: "No leídas", isRead: false },
  read: { label: "Leídas", isRead: true },
};

// Anuncios mostrados por página en el box alargado del dashboard de cliente
const CLIENT_ANNOUNCEMENTS_PAGE_SIZE = 4;

export function Dashboard() {
  const { user: currentUser, interceptorReady } = useAuth();
  const isTicketFreelancer = currentUser?.role === "freelance_ticket";
  const isSuperAdmin = currentUser?.role === "superadmin";
  const isAgencyAdmin = currentUser?.role === "agency_admin";
  const isConsultant = currentUser?.role === "consultant";
  const isAgencyScopedUser = isAgencyAdmin || isConsultant;

  if (isTicketFreelancer) {
    return <TicketFreelancerNotifications />;
  }
  const shouldShowAnnouncementsPanel = isSuperAdmin || isAgencyScopedUser;
  const shouldShowFullWidthTasksPanel = isAgencyScopedUser;
  const rightColumnMaxHeight = "calc(100vh - 280px)";
  const navigate = useNavigate();
  const {
    setPageContext,
    selectedFilter,
    selectedFilterData,
    changeSelectedFilter,
    dashboardRefreshKey,
    filterRefresh,
    filterOptions,
    selectedClientSetupActive,
    selectedClientSetupNotNeeded,
  } = useOutletContext();
  const { theme, colors, neutrals, notificationColors } = useTheme();
  const queryClient = useQueryClient();
  const announcementsPanelRef = React.useRef(null);

  // Obtener endpoint del dashboard según el rol y filtro
  const dashboardEndpoint = () => {
    if (!currentUser) return null;

    if (currentUser.role === "superadmin") {
      if (selectedFilter) {
        return `dashboard/superadmin/?agency=${selectedFilter}`;
      }
      return "dashboard/superadmin/";
    } else if (
      currentUser.role === "agency_admin" ||
      currentUser.role === "consultant"
    ) {
      return "dashboard/agency/";
    } else if (currentUser.role === "client") {
      // Para multicliente: pasar client si hay selectedFilter
      if (selectedFilter) {
        return `dashboard/client?client=${selectedFilter}`;
      }
      return "dashboard/client";
    }
    return null;
  };

  // Determine if we should fetch dashboard data
  // Skip the call for clients with no hired pack and for those whose setup is
  // marked as "no necesario" -- neither renders the itinerary box
  const shouldFetchDashboard =
    interceptorReady &&
    currentUser &&
    !(
      currentUser.role === "client" &&
      (selectedClientSetupActive === false ||
        selectedClientSetupNotNeeded === true)
    );

  // Obtener datos del dashboard desde el backend
  const {
    data: dashboardData,
    error: dashboardError,
    isLoading: dashboardIsLoading,
    isInitialLoading: dashboardIsInitialLoading,
  } = getData(dashboardEndpoint(), shouldFetchDashboard, [
    currentUser?.id,
    dashboardRefreshKey,
    ...(currentUser?.role === "superadmin" || currentUser?.role === "client" ? [filterRefresh] : []),
  ]);

  const isDashboardDataLoading =
    !dashboardData && (dashboardIsLoading || dashboardIsInitialLoading);

  // Modal state - declared early so shouldFetchClients can reference it
  const [isNotificationFilterModalOpen, setIsNotificationFilterModalOpen] =
    useState(false);

  // Lazy load clients only when notification filters modal is open
  const shouldFetchClients = Boolean(
    interceptorReady &&
    currentUser &&
    currentUser.role !== "client" &&
    isNotificationFilterModalOpen
  );

  const { data: clientsResponse } = getData(
    shouldFetchClients ? "clients/options/?ordering=name" : null,
    shouldFetchClients,
    [currentUser?.id, isNotificationFilterModalOpen]
  );

  const clientsList = React.useMemo(() => {
    if (!clientsResponse) return [];
    if (Array.isArray(clientsResponse?.results)) return clientsResponse.results;
    if (Array.isArray(clientsResponse)) return clientsResponse;
    return [];
  }, [clientsResponse]);

  const clientNameById = React.useMemo(() => {
    if (!clientsList.length) return {};
    return clientsList.reduce((acc, client) => {
      const clientId = client?.id ?? client?.pk;
      if (clientId !== undefined && clientId !== null) {
        acc[clientId] =
          client?.name ||
          client?.title ||
          client?.slug ||
          `Cliente #${clientId}`;
      }
      return acc;
    }, {});
  }, [clientsList]);

  const createEmptyNotificationFilters = React.useCallback(
    () => ({
      agency: null,
      client: null,
      date: "",
    }),
    []
  );

  const [appliedNotificationFilters, setAppliedNotificationFilters] = useState(
    () => createEmptyNotificationFilters()
  );
  const [draftNotificationFilters, setDraftNotificationFilters] = useState(() =>
    createEmptyNotificationFilters()
  );

  const [successPopup, setSuccessPopup] = useState({
    isOpen: false,
    message: "",
    error: false,
  });

  const hasAppliedNotificationFilters = React.useMemo(
    () =>
      Boolean(
        appliedNotificationFilters.agency ||
        appliedNotificationFilters.client ||
        appliedNotificationFilters.date
      ),
    [appliedNotificationFilters]
  );

  const notificationAgencyOptions = React.useMemo(() => {
    if (currentUser?.role !== "superadmin") return [];
    const agencies = Array.isArray(filterOptions) ? filterOptions : [];
    return agencies
      .map((agency) => {
        const value = agency.id ?? agency.pk;
        if (value === undefined || value === null) return null;
        return {
          value,
          label: agency.name || agency.title || `Agencia #${value}`,
        };
      })
      .filter(Boolean);
  }, [currentUser?.role, filterOptions]);
  const isLoadingNotificationAgencies = false;

  const notificationClientOptions = React.useMemo(() => {
    if (!clientsList.length) return [];
    return clientsList
      .map((client) => {
        const value = client?.id ?? client?.pk ?? null;
        if (value === null || value === undefined) return null;
        return {
          value,
          label:
            client?.name ||
            client?.title ||
            client?.slug ||
            `Cliente #${value}`,
          agency:
            client?.agency ||
            client?.agency_id ||
            client?.agency?.id ||
            client?.agency_info?.id ||
            null,
        };
      })
      .filter(Boolean);
  }, [clientsList]);

  const globalAgencyOption = React.useMemo(() => {
    if (
      currentUser?.role !== "superadmin" ||
      !selectedFilter ||
      !selectedFilterData?.name
    ) {
      return null;
    }
    return {
      value: selectedFilter,
      label: selectedFilterData.name,
    };
  }, [currentUser?.role, selectedFilter, selectedFilterData]);

  const globalClientOption = React.useMemo(() => {
    if (!isAgencyScopedUser || !selectedFilter || !selectedFilterData?.name) {
      return null;
    }
    return {
      value: selectedFilter,
      label: selectedFilterData.name,
    };
  }, [isAgencyScopedUser, selectedFilter, selectedFilterData]);

  const isGlobalAgencyLocked =
    currentUser?.role === "superadmin" && Boolean(globalAgencyOption);
  const isGlobalClientLocked =
    isAgencyScopedUser && Boolean(globalClientOption);

  const modalAgencyForNotificationClients = React.useMemo(() => {
    if (currentUser?.role !== "superadmin") return null;
    return (
      draftNotificationFilters.agency?.value ||
      appliedNotificationFilters.agency?.value ||
      selectedFilter ||
      null
    );
  }, [
    currentUser?.role,
    draftNotificationFilters.agency,
    appliedNotificationFilters.agency,
    selectedFilter,
  ]);

  const filteredNotificationClientOptions = React.useMemo(() => {
    if (currentUser?.role === "superadmin") {
      if (modalAgencyForNotificationClients) {
        return notificationClientOptions.filter(
          (client) =>
            !client.agency ||
            client.agency === modalAgencyForNotificationClients
        );
      }
      return notificationClientOptions;
    }

    if (isAgencyScopedUser) {
      return notificationClientOptions;
    }

    return [];
  }, [
    currentUser?.role,
    modalAgencyForNotificationClients,
    notificationClientOptions,
    isAgencyScopedUser,
  ]);

  const resolvedNotificationAgencyId = React.useMemo(() => {
    if (currentUser?.role !== "superadmin") return null;
    return appliedNotificationFilters.agency?.value || selectedFilter || null;
  }, [appliedNotificationFilters.agency, selectedFilter, currentUser?.role]);

  const resolvedNotificationClientId = React.useMemo(() => {
    if (currentUser?.role === "superadmin") {
      return appliedNotificationFilters.client?.value || null;
    }
    if (isAgencyScopedUser) {
      return appliedNotificationFilters.client?.value || selectedFilter || null;
    }
    // Para usuarios cliente: filtrar por cliente seleccionado si hay uno
    if (currentUser?.role === "client" && selectedFilter) {
      return selectedFilter;
    }
    return null;
  }, [
    appliedNotificationFilters.client,
    selectedFilter,
    isAgencyScopedUser,
    currentUser?.role,
  ]);

  const resolvedNotificationDate = React.useMemo(
    () => appliedNotificationFilters.date || "",
    [appliedNotificationFilters.date]
  );

  React.useEffect(() => {
    setDraftNotificationFilters(appliedNotificationFilters);
  }, [appliedNotificationFilters]);

  const previousNotificationSelectedFilterRef = React.useRef(selectedFilter);
  React.useEffect(() => {
    if (previousNotificationSelectedFilterRef.current !== selectedFilter) {
      previousNotificationSelectedFilterRef.current = selectedFilter;

      if (
        appliedNotificationFilters.agency ||
        appliedNotificationFilters.client ||
        appliedNotificationFilters.date
      ) {
        const emptyFilters = createEmptyNotificationFilters();
        setAppliedNotificationFilters(emptyFilters);
        setDraftNotificationFilters(emptyFilters);
      }
    }
  }, [
    appliedNotificationFilters,
    createEmptyNotificationFilters,
    selectedFilter,
  ]);

  const shouldFetchNotifications = !!(interceptorReady && currentUser);
  const [notifications, setNotifications] = useState([]);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [clearingThreadId, setClearingThreadId] = useState(null);

  // Task detail modal state (for task_assigned notifications)
  const [selectedNotificationTaskId, setSelectedNotificationTaskId] =
    useState(null);
  const [tasksRefreshKey, setTasksRefreshKey] = useState(0);

  // Filter state for notifications
  const [selectedNotificationFilter, setSelectedNotificationFilter] =
    useState("unread");
  const [notificationPage, setNotificationPage] = useState(0);
  const [notificationPageSize, setNotificationPageSize] = useState(25);

  // === TASKS PAGINATION STATE (Frontend-only) ===
  const [tasksPage, setTasksPage] = useState(0);
  const [tasksPageSize, setTasksPageSize] = useState(5);

  // Build notifications endpoint with filter
  const getNotificationsEndpoint = () => {
    if (!shouldFetchNotifications) return null;

    const baseEndpoint = "dashboard/notifications/";
    const filterConfig =
      NOTIFICATION_STATUS_FILTERS[selectedNotificationFilter];

    const params = new URLSearchParams();

    if (resolvedNotificationAgencyId) {
      params.set("agency", resolvedNotificationAgencyId);
    }

    if (resolvedNotificationClientId) {
      params.set("client_id", resolvedNotificationClientId);
    }

    if (resolvedNotificationDate) {
      params.set("date", resolvedNotificationDate);
    }

    // Add read status filter if configured
    if (filterConfig && filterConfig.isRead !== undefined) {
      params.set("is_read", filterConfig.isRead ? "true" : "false");
    }

    params.set("page", (notificationPage + 1).toString());
    params.set("page_size", notificationPageSize.toString());

    const queryString = params.toString();
    return queryString ? `${baseEndpoint}?${queryString}` : baseEndpoint;
  };

  const {
    data: notificationsResponse,
    error: notificationsError,
    isLoading: notificationsIsLoading,
    isInitialLoading: notificationsIsInitialLoading,
  } = getData(getNotificationsEndpoint(), shouldFetchNotifications, [
    currentUser?.id,
    dashboardRefreshKey,
    selectedNotificationFilter,
    currentUser?.role,
    filterRefresh,
    resolvedNotificationAgencyId,
    resolvedNotificationClientId,
    resolvedNotificationDate,
    notificationPage,
    notificationPageSize,
  ]);

  React.useEffect(() => {
    if (!notificationsResponse) {
      setNotifications([]);
      return;
    }

    const normalizedResults = Array.isArray(notificationsResponse)
      ? notificationsResponse
      : notificationsResponse.results || [];

    setNotifications(normalizedResults);
  }, [notificationsResponse]);

  const unreadNotificationsCount = React.useMemo(() => {
    if (selectedNotificationFilter === "unread") {
      return notificationsResponse?.count ?? 0;
    }
    return 0; // We don't have the unread count if we are filtering by read, unless we make another request.
  }, [notificationsResponse, selectedNotificationFilter]);

  const totalNotificationsCount = notificationsResponse?.count ?? 0;

  const isNotificationsLoading =
    !notificationsResponse &&
    (notificationsIsLoading || notificationsIsInitialLoading);
  const isUnreadCountLoading = isNotificationsLoading;
  const notificationsTotalForPagination = React.useMemo(() => {
    if (Array.isArray(notificationsResponse)) {
      return notificationsResponse.length;
    }
    if (notificationsResponse?.count !== undefined) {
      return notificationsResponse.count;
    }
    if (Array.isArray(notificationsResponse?.results)) {
      return notificationsResponse.results.length;
    }
    return totalNotificationsCount || 0;
  }, [notificationsResponse, totalNotificationsCount]);

  React.useEffect(() => {
    setNotificationPage(0);
  }, [
    selectedNotificationFilter,
    resolvedNotificationAgencyId,
    resolvedNotificationClientId,
    resolvedNotificationDate,
    currentUser?.role,
    filterRefresh,
    dashboardRefreshKey,
  ]);

  React.useEffect(() => {
    if (isNotificationsLoading || notificationsIsInitialLoading) return;

    const total = notificationsTotalForPagination;
    if (!total) {
      if (notificationPage !== 0) {
        setNotificationPage(0);
      }
      return;
    }

    const maxPage = Math.max(Math.ceil(total / notificationPageSize) - 1, 0);
    if (notificationPage > maxPage) {
      setNotificationPage(maxPage);
    }
  }, [
    notificationsTotalForPagination,
    notificationPageSize,
    notificationPage,
    isNotificationsLoading,
    notificationsIsInitialLoading,
  ]);

  const hasNotifications = notifications.length > 0;

  const defaultNotificationColor = React.useMemo(
    () => ({
      text: notificationColors?.default?.text || neutrals.gray[600],
      dot: notificationColors?.default?.dot || neutrals.gray[400],
      background: notificationColors?.default?.background || neutrals.gray[100],
    }),
    [notificationColors, neutrals]
  );

  const getNotificationColorConfig = React.useCallback(
    (type) => {
      if (type && notificationColors?.[type]) {
        return notificationColors[type];
      }
      return defaultNotificationColor;
    },
    [notificationColors, defaultNotificationColor]
  );
  // Set page context based on user role
  React.useEffect(() => {
    if (currentUser) {
      const getPageInfo = () => {
        // Breadcrumb para superadmin
        let breadcrumbItems = null;
        if (currentUser.role === "superadmin") {
          if (selectedFilter) {
            breadcrumbItems = [
              {
                label: "Vista Global",
                onClick: () => changeSelectedFilter("global"),
              },
              { label: `Agencia: ${selectedFilterData.name}` },
            ];
          } else {
            breadcrumbItems = [{ label: "Vista Global" }];
          }
        }

        switch (currentUser.role) {
          case "client":
            return {
              title: "Panel de Cliente",
              subtitle: `Bienvenido de vuelta, ${currentUser.name}`,
              showTitle: true,
            };
          case "superadmin":
            return {
              title: "Dashboard",
              subtitle: selectedFilter
                ? "Vista filtrada por agencia"
                : "Vista general de todas las operaciones",
              showTitle: true,
              breadcrumbItems,
            };
          case "agency_admin":
          case "consultant":
            return {
              title: "Dashboard",
              subtitle: "Resumen de tu agencia",
              showTitle: true,
            };
          default:
            return {
              title: "Dashboard",
              subtitle: "Resumen de la plataforma",
              showTitle: true,
            };
        }
      };
      setPageContext(getPageInfo());
    }
  }, [currentUser, setPageContext, selectedFilter, selectedFilterData]);

  // Early return if user is not loaded
  if (!currentUser) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 256,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              border: `4px solid ${colors.primary.main}`,
              borderTopColor: "transparent",
              borderRadius: "50%",
              margin: "0 auto 8px",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary }}
          >
            Cargando dashboard...
          </Typography>
        </Box>
      </Box>
    );
  }

  const getMetricsForRole = () => {
    if (!currentUser || !dashboardData) return {};

    const kpis = dashboardData.kpis || {};

    if (currentUser.role === "superadmin" && !selectedFilter) {
      // Superadmin sin filtro - vista global
      return {
        totalAgencies: kpis.total_agencies || 0,
        archivedAgencies: kpis.archived_agencies,
        newAgencies: kpis.recent_agencies || 0,
        totalClients: kpis.total_clients || 0,
        newClients: kpis.recent_clients || 0,
      };
    } else if (
      currentUser.role === "agency_admin" ||
      currentUser.role === "consultant" ||
      (currentUser.role === "superadmin" && selectedFilter)
    ) {
      // Agency admin y consultant
      return {
        totalClients: kpis.total_clients || 0,
        newClients: kpis.recent_clients || 0,
        pendingOnboardings: kpis.pending_onboardings || 0,
        activeSetups: kpis.active_setups || 0,
      };
    }

    return {};
  };

  const metrics = getMetricsForRole();
  const tasksInProgress = dashboardData?.tasks_in_progress || [];
  const hasTasksInProgress = tasksInProgress.length > 0;

  // Frontend pagination for tasks
  const paginatedTasks = React.useMemo(() => {
    const startIndex = tasksPage * tasksPageSize;
    const endIndex = startIndex + tasksPageSize;
    return tasksInProgress.slice(startIndex, endIndex);
  }, [tasksInProgress, tasksPage, tasksPageSize]);

  const topTickets = dashboardData?.top_tickets || [];
  const hasTopTickets = topTickets.length > 0;
  const dashboardErrorMessage = getApiErrorMessage(dashboardError);
  const normalizedDashboardErrorMessage = dashboardErrorMessage
    .toLowerCase()
    .trim();
  // A setup explicitly marked as "no necesario" is not something the client can
  // hire: hide the itinerary box altogether instead of showing the empty state.
  const shouldHideClientItinerary =
    currentUser?.role === "client" && selectedClientSetupNotNeeded === true;
  const shouldShowClientItineraryEmptyState =
    currentUser?.role === "client" &&
    !shouldHideClientItinerary &&
    (selectedClientSetupActive === false ||
      normalizedDashboardErrorMessage.includes(CLIENT_NO_SETUP_PROJECT_MESSAGE));
  const shouldShowClientItinerarySkeleton =
    currentUser?.role === "client" &&
    !shouldHideClientItinerary &&
    selectedClientSetupActive !== false &&
    isDashboardDataLoading;

  const clientItinerarySkeleton = (
    <Card
      sx={{
        border: `1px solid ${colors.primary.light}33`,
        backgroundColor: `${colors.primary.light}08`,
      }}
    >
      <CardHeader>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Skeleton variant="text" width="35%" height={24} />
          <Skeleton variant="rounded" width={110} height={36} />
        </Box>
      </CardHeader>
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Skeleton variant="text" width="30%" height={20} />
          <Skeleton variant="text" width={60} height={20} />
        </Box>
        <Skeleton variant="rounded" height={14} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          {Array.from({ length: 2 }).map((_, index) => (
            <Box key={`client-itinerary-card-skeleton-${index}`}>
              <Skeleton variant="text" width="50%" height={32} />
              <Skeleton variant="text" width="70%" height={20} />
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            backgroundColor: "#eff6ff",
            p: 1.5,
            borderRadius: 1.5,
            border: `1px solid #bfdbfe`,
          }}
        >
          <Skeleton variant="text" width="40%" height={20} />
          <Skeleton variant="text" width="60%" height={18} sx={{ mt: 1 }} />
        </Box>
      </CardContent>
    </Card>
  );

  // === NOTIFICATION HELPER FUNCTIONS ===
  // These must be defined before the client section early return

  const getNotificationRoute = (notification) =>
    getNotificationRouteForRole(notification, currentUser?.role);

  const handleGlobalWallMessageOpen = (message) => {
    if (!message) return;

    const targetClientId =
      message.client_id ||
      message.client?.id ||
      (typeof message.client === "number" ? message.client : null);
    const targetThreadId =
      message.thread?.id ||
      message.thread_id ||
      (typeof message.thread === "number" ? message.thread : null);

    if (!targetClientId || !targetThreadId) {
      setSuccessPopup({
        isOpen: true,
        message:
          "No se pudo abrir este mensaje porque falta información del cliente o hilo.",
        error: true,
      });
      return;
    }

    const pageSize = 10;
    const position = Number(message.message_position) || 0;
    const page = Math.max(1, Math.floor(position / pageSize) + 1);
    const params = new URLSearchParams();

    if (page > 1) {
      params.set("page", String(page));
    }
    if (message.id) {
      params.set("highlightMessage", String(message.id));
    }

    const messagePosition =
      message.message_position ?? message.messagePosition ?? null;
    if (messagePosition != null) {
      params.set("messagePosition", String(messagePosition));
    }

    const isSynchronizedThreadMessage =
      message?.is_thread === true ||
      String(message?.is_thread).toLowerCase() === "true";

    if (isSynchronizedThreadMessage && message?.id) {
      params.set("mt", encodeUrlSafeBase64(message.id));
      const synchronizedThreadTitle = (message?.is_thread_title || "").trim();
      if (synchronizedThreadTitle) {
        params.set("mtt", synchronizedThreadTitle);
      }
    }

    const targetUrl =
      params.size > 0
        ? `/conversations/${targetClientId}/${targetThreadId}?${params.toString()}`
        : `/conversations/${targetClientId}/${targetThreadId}`;

    navigate(targetUrl);
  };

  const handleGlobalWallNotification = ({ message, isError }) => {
    if (!message) return;
    setSuccessPopup({
      isOpen: true,
      message,
      error: Boolean(isError),
    });
  };

  const openCentralAnnouncementFromNotification = React.useCallback(
    (notification) => {
      if (!notification) return false;
      const panelApi = announcementsPanelRef.current;
      if (!panelApi || typeof panelApi.openAnnouncement !== "function") {
        return false;
      }

      const announcementId =
        notification.data?.announcement_id ||
        notification.data?.announcement?.id ||
        notification.data?.id ||
        notification.announcement_id;

      if (!announcementId) return false;

      panelApi.openAnnouncement({
        announcementId,
        position: notification.data?.position,
      });
      return true;
    },
    []
  );

  // Anuncio o tarea pulsados en la campana del header: se abren al montar el panel
  const location = useLocation();
  const pendingNotificationRef = React.useRef(null);
  React.useEffect(() => {
    if (location.state?.openNotification) {
      pendingNotificationRef.current = location.state.openNotification;
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);
  React.useEffect(() => {
    const pending = pendingNotificationRef.current;
    if (!pending) return;
    if (pending.type === "task_assigned") {
      setSelectedNotificationTaskId(pending.task_id);
    } else if (!openCentralAnnouncementFromNotification(pending)) {
      return;
    }
    pendingNotificationRef.current = null;
  });

  const invalidateNotificationQueries = React.useCallback(
    () => invalidateNotificationQueriesFor(queryClient),
    [queryClient]
  );

  const markNotificationsAsRead = React.useCallback(
    async (ids, { silent = false } = {}) => {
      if (!ids || ids.length === 0) return;
      const idsSet = new Set(ids);
      const previousNotifications = notifications;
      const readAtTimestamp = new Date().toISOString();

      setNotifications((prev) =>
        prev.map((notification) =>
          idsSet.has(notification.id)
            ? {
              ...notification,
              is_read: true,
              read_at: notification.read_at || readAtTimestamp,
            }
            : notification
        )
      );

      try {
        await apiClient.post(
          "dashboard/notifications/mark-read/",
          { ids },
          { withCredentials: true }
        );
        invalidateNotificationQueries();
      } catch (error) {
        console.error("Error al marcar notificaciones como leídas:", error);
        setNotifications(previousNotifications);
      }
    },
    [invalidateNotificationQueries, notifications]
  );

  const handleMarkAllNotificationsRead = React.useCallback(async () => {
    if (!hasNotifications) return;
    const previousNotifications = notifications;
    const readAtTimestamp = new Date().toISOString();

    setIsMarkingAllRead(true);
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.is_read
          ? notification
          : { ...notification, is_read: true, read_at: readAtTimestamp }
      )
    );

    try {
      await apiClient.post(
        "dashboard/notifications/mark-all-read/",
        {},
        { withCredentials: true }
      );
      invalidateNotificationQueries();
    } catch (error) {
      console.error("Error al marcar todas las notificaciones:", error);
      setNotifications(previousNotifications);
    } finally {
      setIsMarkingAllRead(false);
    }
  }, [hasNotifications, invalidateNotificationQueries, notifications]);

  const clearThreadNotifications = React.useCallback(
    async (threadId) => {
      if (!threadId) return;
      const previousNotifications = notifications;
      const readAtTimestamp = new Date().toISOString();

      setClearingThreadId(threadId);
      setNotifications((prev) =>
        prev
          .filter(
            (notification) =>
              !(
                notification.thread_id === threadId &&
                notification.type === "unread_in_thread"
              )
          )
          .map((notification) =>
            notification.thread_id === threadId
              ? {
                ...notification,
                is_read: true,
                read_at: notification.read_at || readAtTimestamp,
              }
              : notification
          )
      );

      try {
        await apiClient.post(
          "dashboard/notifications/clear-thread/",
          { thread_id: threadId },
          { withCredentials: true }
        );
        invalidateNotificationQueries();
      } catch (error) {
        console.error("Error al limpiar notificaciones del hilo:", error);
        setNotifications(previousNotifications);
      } finally {
        setClearingThreadId(null);
      }
    },
    [invalidateNotificationQueries, notifications]
  );

  const handleMarkNotificationRead = React.useCallback(
    (notificationId) => {
      const target = notifications.find(
        (notification) => notification.id === notificationId
      );
      if (!target || target.is_read) return;
      markNotificationsAsRead([notificationId]);
    },
    [notifications, markNotificationsAsRead]
  );

  const handleNotificationClick = (notification) => {
    if (!notification) return;

    if (notification.type === "central_announcement") {
      openCentralAnnouncementFromNotification(notification);
      if (!notification.is_read) {
        markNotificationsAsRead([notification.id], { silent: true });
      }
      return;
    }

    if (notification.type === "task_assigned" && notification.task_id) {
      setSelectedNotificationTaskId(notification.task_id);
      if (!notification.is_read) {
        markNotificationsAsRead([notification.id], { silent: true });
      }
      return;
    }

    // Handle content_review and content_updated notifications
    if (
      notification.type === "content_review" ||
      notification.type === "content_updated"
    ) {
      if (!notification.is_read) {
        markNotificationsAsRead([notification.id], { silent: true });
      }

      navigate(getContentNotificationRoute(notification, currentUser?.role));
      return;
    }

    const destination = getNotificationRoute(notification);
    if (!destination) return;

    if (notification.thread_id && notification.type === "unread_in_thread") {
      clearThreadNotifications(notification.thread_id);
    } else if (!notification.is_read) {
      markNotificationsAsRead([notification.id], { silent: true });
    }

    if (isSupportTicketNotification(notification)) {
      openSupportTicket(notification.ticket_id);
      return;
    }

    navigate(destination);
  };

  // === NOTIFICATION HANDLERS (must be before early return) ===
  const handleNotificationPageChange = React.useCallback((event, newPage) => {
    setNotificationPage(newPage);
  }, []);

  const handleNotificationRowsPerPageChange = React.useCallback((event) => {
    const newSize = parseInt(event.target.value, 10);
    if (Number.isNaN(newSize)) return;
    setNotificationPageSize(newSize);
    setNotificationPage(0);
  }, []);

  // === TASKS PAGINATION HANDLERS (must be before early return) ===
  const handleTasksPageChange = React.useCallback((event, newPage) => {
    setTasksPage(newPage);
  }, []);

  const handleTasksRowsPerPageChange = React.useCallback((event) => {
    const newSize = parseInt(event.target.value, 10);
    if (Number.isNaN(newSize)) return;
    setTasksPageSize(newSize);
    setTasksPage(0);
  }, []);

  // Contar filtros aplicados en notificaciones
  const activeNotificationFiltersCount = React.useMemo(() => {
    let count = 0;

    // Contar filtros aplicados manualmente
    if (
      appliedNotificationFilters.agency &&
      appliedNotificationFilters.agency.value
    )
      count += 1;
    if (
      appliedNotificationFilters.client &&
      appliedNotificationFilters.client.value
    )
      count += 1;
    if (
      appliedNotificationFilters.date &&
      appliedNotificationFilters.date.trim() !== ""
    )
      count += 1;

    // Contar filtros globales de AppLayout (si no hay filtro manual aplicado)
    if (
      currentUser?.role === "superadmin" &&
      !appliedNotificationFilters.agency?.value &&
      selectedFilter
    ) {
      count += 1; // El filtro global de superadmin es de agencia
    }
    if (
      isAgencyScopedUser &&
      !appliedNotificationFilters.client?.value &&
      selectedFilter
    ) {
      count += 1; // El filtro global para agencias/consultores es de cliente
    }

    return count;
  }, [
    appliedNotificationFilters,
    currentUser?.role,
    selectedFilter,
    isAgencyScopedUser,
  ]);

  const hasNotificationFiltersApplied = activeNotificationFiltersCount > 0;

  const notificationFilterConfigs = React.useMemo(
    () => [
      {
        key: "agency",
        title: "Agencia",
        description:
          "Escoge la agencia sobre la que quieres filtrar las notificaciones.",
        icon: Building2,
        options: notificationAgencyOptions,
        loading: isLoadingNotificationAgencies,
        disabled: isGlobalAgencyLocked,
        placeholder: "Selecciona una agencia",
        loadingPlaceholder: "Cargando agencias...",
        fallbackOption: globalAgencyOption,
        showForRoles: ["superadmin"],
        role: currentUser?.role,
      },
      {
        key: "client",
        title: "Cliente",
        description:
          "Filtra las notificaciones asociadas a un cliente concreto.",
        icon: Users,
        options: filteredNotificationClientOptions,
        loading: false,
        disabled: isGlobalClientLocked,
        placeholder: "Selecciona un cliente",
        loadingPlaceholder: "Cargando clientes...",
        fallbackOption:
          currentUser?.role === "superadmin" ? null : globalClientOption,
        showForRoles: ["superadmin", "agency_admin", "consultant"],
        role: currentUser?.role,
      },
      {
        key: "date",
        title: "Fecha exacta",
        description: "Muestra las notificaciones de un día concreto.",
        icon: Calendar,
        type: "date",
        includeAny: false,
        role: currentUser?.role,
        showForRoles: ["superadmin", "agency_admin", "consultant", "client"],
      },
    ],
    [
      currentUser?.role,
      filteredNotificationClientOptions,
      globalAgencyOption,
      globalClientOption,
      isGlobalAgencyLocked,
      isGlobalClientLocked,
      isLoadingNotificationAgencies,
      notificationAgencyOptions,
    ]
  );

  const handleOpenNotificationFilters = () =>
    setIsNotificationFilterModalOpen(true);
  const handleCloseNotificationFilters = () =>
    setIsNotificationFilterModalOpen(false);
  const handleDraftNotificationFilterChange = (key, value) => {
    setDraftNotificationFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const handleApplyNotificationFilters = () => {
    setAppliedNotificationFilters({ ...draftNotificationFilters });
    setIsNotificationFilterModalOpen(false);
    setNotifications([]);
    setNotificationPage(0);
  };
  const handleClearNotificationDraftFilters = () => {
    const emptyFilters = createEmptyNotificationFilters();
    setDraftNotificationFilters(emptyFilters);
  };

  // Handle notification filter change
  const handleNotificationFilterChange = (filter) => {
    if (filter === selectedNotificationFilter) return;
    setSelectedNotificationFilter(filter);
    setNotifications([]);
    setNotificationPage(0);
  };

  const shouldRenderLegacyTasksPanel = Boolean(
    dashboardData?.show_legacy_tasks_panel
  );
  const clientProgressProjectType = dashboardData?.kpis?.progress_project_type || "setup";
  const clientProgressProjectId = dashboardData?.kpis?.progress_project_id || null;
  const isClientProgressRecurrent =
    dashboardData?.kpis?.is_recurrent_progress_project ??
    (clientProgressProjectType === "recurrent_monthly");
  const currentMonthLabel = new Intl.DateTimeFormat("es-ES", {
    month: "long",
  }).format(new Date());
  const currentMonthTitle =
    currentMonthLabel.charAt(0).toUpperCase() + currentMonthLabel.slice(1);
  const clientProgressTitle = isClientProgressRecurrent
    ? `Progreso de tu Proyecto Mensual: ${currentMonthTitle}`
    : "Progreso de tu Itinerario";
  const clientProgressDetailsRoute = isClientProgressRecurrent
    ? (clientProgressProjectId
        ? `/projects/custom/${clientProgressProjectId}`
        : "/projects/custom")
    : (clientProgressProjectId ? `/setup/${clientProgressProjectId}` : "/setup");

  const getClientCardProgressTitle = (clientData) =>
    (clientData?.is_recurrent_progress_project ??
      (clientData?.progress_project_type === "recurrent_monthly"))
      ? `Progreso de tu Proyecto Mensual: ${currentMonthTitle}`
      : "Progreso de tu Itinerario";

  const getClientCardProgressRoute = (clientData) => {
    const isRecurrentClientProgress =
      clientData?.is_recurrent_progress_project ??
      (clientData?.progress_project_type === "recurrent_monthly");
    if (isRecurrentClientProgress) {
      return clientData?.progress_project_id
        ? `/projects/custom/${clientData.progress_project_id}`
        : "/projects/custom";
    }
    return clientData?.setup_project_id
      ? `/setup/${clientData.setup_project_id}`
      : "/setup";
  };

  // Vista específica para Cliente
  if (currentUser.role === "client") {
    // Detectar modo multicliente desde el backend
    const isMultiClientResponse = dashboardData?.is_multi_client === true;
    const clientsProgress = (dashboardData?.clients_progress || []).filter(
      (clientData) => clientData?.setup_not_needed !== true
    );

    // Vista multicliente sin filtro: mostrar boxes de progreso de cada empresa
    if (isMultiClientResponse && !selectedFilter) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Boxes de progreso de itinerario por empresa */}
          {shouldShowClientItinerarySkeleton ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
                gap: 3,
              }}
            >
              {Array.from({ length: 2 }).map((_, index) => (
                <Card key={`multi-skeleton-${index}`}>
                  <CardHeader>
                    <Skeleton variant="text" width="60%" height={24} />
                  </CardHeader>
                  <CardContent>
                    <Skeleton variant="rounded" height={14} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="40%" height={20} />
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : clientsProgress.length === 0 ? null : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" },
                gap: 3,
              }}
            >
              {clientsProgress.map((clientData) => (
                <Card
                  key={clientData.client_id}
                  sx={{
                    border: `1px solid ${colors.primary.light}55`,
                    backgroundColor: `${colors.primary.light}11`,
                  }}
                >
                  <CardHeader sx={{ pt: "14px", pb: 1.25 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <CardTitle
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Target size={20} color={colors.primary.main} />
                        {getClientCardProgressTitle(clientData)} ·{" "}
                        {clientData.client_name}
                      </CardTitle>
                      {clientData.has_setup_project && (
                        <Button
                          variant="outline"
                          onClick={() => navigate(getClientCardProgressRoute(clientData))}
                        >
                          <Eye size={16} style={{ marginRight: 8 }} />
                          Ver Completo
                        </Button>
                      )}
                    </Box>
                  </CardHeader>
                  <CardContent sx={{ pt: 1.25, pb: 2 }}>
                    {clientData.has_setup_project ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Completitud General
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {Math.round(clientData.setup_progress || 0)}%
                          </Typography>
                        </Box>
                        <Progress value={Math.round(clientData.setup_progress || 0)} />
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                            mt: 0.5,
                          }}
                        >
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              variant="h5"
                              sx={{ color: "#0F9D58", fontWeight: 600 }}
                            >
                              {clientData.completed_tasks || 0}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              Tareas completadas de {clientData.total_tasks || 0}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              variant="h5"
                              sx={{ color: "#0F9D58", fontWeight: 600 }}
                            >
                              {clientData.completed_phases || 0}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              Fases completadas de {clientData.total_phases || 0}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <EmptyStateMessage
                        icon={Settings}
                        message={getSetupUnavailableMessage(currentUser)}
                        sx={{ py: 2 }}
                      />
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 11fr) minmax(0, 9fr)" },
              gap: 4,
            }}
          >
            <Card
              sx={{
                maxHeight: { xs: "none", lg: "calc(100vh - 380px)" },
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <GlobalMessageWall
                onMessageOpen={handleGlobalWallMessageOpen}
                refreshKey={dashboardRefreshKey}
                filterRefresh={filterRefresh}
                currentUser={currentUser}
                interceptorReady={interceptorReady}
                enabled={true}
                clients={clientsProgress.map((clientData) => ({
                  id: clientData.client_id,
                  name: clientData.client_name,
                }))}
                selectedFilter={selectedFilter}
                selectedClientId={selectedFilter || null}
                userRole={currentUser?.role}
                conversationFontSize={currentUser?.ui_preferences?.conversation_font_size}
                onShowNotification={handleGlobalWallNotification}
                forceShowClientMeta={true}
              />
            </Card>

            <NotificationPanel
              notifications={notifications}
              isLoading={isNotificationsLoading}
              error={notificationsError}
              unreadCount={unreadNotificationsCount}
              isUnreadCountLoading={isUnreadCountLoading}
              onMarkAllRead={handleMarkAllNotificationsRead}
              onMarkRead={handleMarkNotificationRead}
              onNotificationClick={handleNotificationClick}
              isMarkingAllRead={isMarkingAllRead}
              clearingThreadId={clearingThreadId}
              currentUser={currentUser}
              clientNameById={clientNameById}
              getNotificationSummary={getNotificationSummary}
              getNotificationRoute={getNotificationRoute}
              selectedNotificationFilter={selectedNotificationFilter}
              hasNotifications={hasNotifications}
              sx={{
                maxHeight: { xs: "none", lg: "calc(100vh - 380px)" },
              }}
              showFilters={true}
              onFilterChange={handleNotificationFilterChange}
              filterRightContent={
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <Tooltip title="Editar filtros" arrow>
                    <Button
                      variant="outline"
                      size="sm"
                      sx={{
                        borderColor: hasNotificationFiltersApplied
                          ? neutrals.gray[300]
                          : neutrals.gray[200],
                        color: neutrals.gray[600],
                        flexShrink: 0,
                        minWidth: "32px",
                        height: "32px",
                        padding: "4px",
                        backgroundColor: "white",
                      }}
                      aria-label="Abrir filtros avanzados"
                      aria-pressed={hasNotificationFiltersApplied}
                      onClick={handleOpenNotificationFilters}
                    >
                      <SlidersHorizontal size={16} />
                    </Button>
                  </Tooltip>
                  {hasNotificationFiltersApplied && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-7px",
                        right: "-7px",
                        minWidth: "18px",
                        height: "18px",
                        borderRadius: "9px",
                        backgroundColor: neutrals.gray[400],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        fontWeight: 700,
                        color: "white",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      {activeNotificationFiltersCount}
                    </Box>
                  )}
                </Box>
              }
              showPagination={true}
              page={notificationPage}
              rowsPerPage={notificationPageSize}
              totalCount={notificationsTotalForPagination}
              onPageChange={handleNotificationPageChange}
              onRowsPerPageChange={handleNotificationRowsPerPageChange}
              sx={{
                height: { xs: "auto", lg: "100%" },
                maxHeight: { xs: "none", lg: "calc(100vh - 380px)" },
                overflow: { xs: "visible", lg: "hidden" },
              }}
            />
          </Box>

          {/* Anuncios: box alargado bajo notificaciones y mensajes recientes */}
          <AnnouncementsPanel
            ref={announcementsPanelRef}
            currentUser={currentUser}
            interceptorReady={interceptorReady}
            dashboardRefreshKey={dashboardRefreshKey}
            defaultPageSize={CLIENT_ANNOUNCEMENTS_PAGE_SIZE}
            maxHeight="none"
          />

          {/* Modales */}
          <IncidentFiltersModal
            open={isNotificationFilterModalOpen}
            onClose={handleCloseNotificationFilters}
            title="Filtros avanzados"
            filterConfigs={notificationFilterConfigs}
            draftFilters={draftNotificationFilters}
            onChange={handleDraftNotificationFilterChange}
            onApply={handleApplyNotificationFilters}
            onClearAll={handleClearNotificationDraftFilters}
          />

          <SuccessPopup
            isOpen={successPopup.isOpen}
            message={successPopup.message}
            error={successPopup.error}
            onClose={() =>
              setSuccessPopup((prev) => ({ ...prev, isOpen: false }))
            }
          />
        </Box>
      );
    }

    // Vista cliente normal (un solo cliente o con filtro seleccionado)
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Progreso del Setup/Itinerario */}
        {shouldHideClientItinerary ? null : shouldShowClientItinerarySkeleton ? (
          clientItinerarySkeleton
        ) : shouldShowClientItineraryEmptyState ? (
          <Card>
            <CardHeader>
              <CardTitle
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Target size={20} />
                Progreso de tu Itinerario
              </CardTitle>
            </CardHeader>
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 2.5,
              }}
            >
              <EmptyStateMessage
                icon={Settings}
                message={getSetupUnavailableMessage(currentUser)}
              />
            </CardContent>
          </Card>
        ) : (
          <Card
            sx={{
              border: `1px solid ${colors.primary.light}55`,
              backgroundColor: `${colors.primary.light}11`,
            }}
          >
            <CardHeader sx={{ pt: "14px", pb: 1.25 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <CardTitle
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Target size={20} color={colors.primary.main} />
                    {clientProgressTitle}
                  </CardTitle>
                </Box>
                <Button
                  variant="outline"
                  onClick={() => navigate(clientProgressDetailsRoute)}
                >
                  <Eye size={16} style={{ marginRight: 8 }} />
                  Ver Completo
                </Button>
              </Box>
            </CardHeader>
            <CardContent
              sx={{ flex: 1, display: "flex", flexDirection: "column", pt: 1.25, pb: 2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  flex: 1,
                  minHeight: 0,
                  maxHeight: "calc(100vh - 380px)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Completitud General
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {Math.round(dashboardData?.kpis?.setup_progress || 0)}%
                  </Typography>
                </Box>
                <Progress
                  value={Math.round(dashboardData?.kpis?.setup_progress || 0)}
                />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr" },
                    gap: 2,
                    mt: 1,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h5"
                      sx={{ color: "#0F9D58", fontWeight: 600 }}
                    >
                      {dashboardData?.kpis?.completed_tasks || 0}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Tareas completadas de{" "}
                      {dashboardData?.kpis?.total_tasks || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h5"
                      sx={{ color: "#0F9D58", fontWeight: 600 }}
                    >
                      {dashboardData?.kpis?.completed_phases || 0}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      Fases completadas de{" "}
                      {dashboardData?.kpis?.total_phases || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Proyectos y Conversaciones */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 11fr) minmax(0, 9fr)" },
            gap: 4,
          }}
        >
          <Card
            sx={{
              maxHeight: { xs: "none", lg: "calc(100vh - 380px)" },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <GlobalMessageWall
              onMessageOpen={handleGlobalWallMessageOpen}
              refreshKey={dashboardRefreshKey}
              filterRefresh={filterRefresh}
              currentUser={currentUser}
              interceptorReady={interceptorReady}
              enabled={true}
              clients={clientsList}
              selectedFilter={selectedFilter}
              selectedClientId={selectedFilter || currentUser?.client || null}
              userRole={currentUser?.role}
              conversationFontSize={currentUser?.ui_preferences?.conversation_font_size}
              onShowNotification={handleGlobalWallNotification}
            />
          </Card>

          <NotificationPanel
            notifications={notifications}
            isLoading={isNotificationsLoading}
            error={notificationsError}
            unreadCount={unreadNotificationsCount}
            isUnreadCountLoading={isUnreadCountLoading}
            onMarkAllRead={handleMarkAllNotificationsRead}
            onMarkRead={handleMarkNotificationRead}
            onNotificationClick={handleNotificationClick}
            isMarkingAllRead={isMarkingAllRead}
            clearingThreadId={clearingThreadId}
            currentUser={currentUser}
            clientNameById={clientNameById}
            getNotificationSummary={getNotificationSummary}
            getNotificationRoute={getNotificationRoute}
            selectedNotificationFilter={selectedNotificationFilter}
            hasNotifications={hasNotifications}
            sx={{
              maxHeight: { xs: "none", lg: "calc(100vh - 380px)" },
            }}
            showFilters={true}
            onFilterChange={handleNotificationFilterChange}
            filterRightContent={
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <Tooltip title="Editar filtros" arrow>
                  <Button
                    variant="outline"
                    size="sm"
                    sx={{
                      borderColor: hasNotificationFiltersApplied
                        ? neutrals.gray[300]
                        : neutrals.gray[200],
                      color: neutrals.gray[600],
                      flexShrink: 0,
                      minWidth: "32px",
                      height: "32px",
                      padding: "4px",
                      backgroundColor: "white",
                    }}
                    aria-label="Abrir filtros avanzados"
                    aria-pressed={hasNotificationFiltersApplied}
                    onClick={handleOpenNotificationFilters}
                  >
                    <SlidersHorizontal size={16} />
                  </Button>
                </Tooltip>
                {hasNotificationFiltersApplied && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-7px",
                      right: "-7px",
                      minWidth: "18px",
                      height: "18px",
                      borderRadius: "9px",
                      backgroundColor: neutrals.gray[400],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: "white",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {activeNotificationFiltersCount}
                  </Box>
                )}
              </Box>
            }
            showPagination={true}
            page={notificationPage}
            rowsPerPage={notificationPageSize}
            totalCount={notificationsTotalForPagination}
            onPageChange={handleNotificationPageChange}
            onRowsPerPageChange={handleNotificationRowsPerPageChange}
            sx={{
              height: { xs: "auto", lg: "100%" },
              maxHeight: { xs: "none", lg: "calc(100vh - 380px)" },
              overflow: { xs: "visible", lg: "hidden" },
            }}
          />

          {/* Box de tareas en progreso desactivado temporalmente para reutilizarlo más adelante */}
          {shouldRenderLegacyTasksPanel && (shouldShowClientItineraryEmptyState ? (
            <Card
              sx={{
                maxHeight: { xs: "none", lg: "calc(100vh - 380px)" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardHeader>
                <CardTitle
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <ClipboardList size={20} />
                  Tareas en Progreso
                </CardTitle>
              </CardHeader>
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <EmptyStateMessage
                  icon={ClipboardList}
                  message={isExternalAgency(currentUser) ? "Esta funcionalidad no está activa para este cliente." : "Contrata un pack para adquirir esta funcionalidad"}
                />
              </CardContent>
            </Card>
          ) : (
            <Card
              sx={{
                maxHeight: { xs: "none", lg: "calc(100vh - 380px)" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardHeader>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <CardTitle
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <ClipboardList size={20} />
                    Tareas en Progreso
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(clientProgressDetailsRoute)}
                  >
                    Ver todas
                    <ArrowRight size={12} style={{ marginLeft: 4 }} />
                  </Button>
                </Box>
              </CardHeader>
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  maxHeight: "100%",
                  overflow: "hidden",
                  pb: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    flex: 1,
                    minHeight: 0,
                    pb: "16px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    pr: 1,
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: neutrals.gray[100],
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: neutrals.gray[300],
                      borderRadius: "4px",
                    },
                  }}
                >
                  {isDashboardDataLoading ? (
                    Array.from({ length: 3 }).map((_, index) => (
                      <Box
                        key={`task-skeleton-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                          p: 1.5,
                          border: `1px solid ${neutrals.gray[200]}`,
                          borderRadius: "12px",
                        }}
                      >
                        <Skeleton
                          variant="circular"
                          width={10}
                          height={10}
                          sx={{ mt: 0.5 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Skeleton variant="text" width="70%" height={20} />
                          <Skeleton variant="text" width="45%" height={18} />
                        </Box>
                        <Skeleton variant="rounded" width={56} height={20} />
                      </Box>
                    ))
                  ) : (
                    <>
                      {paginatedTasks.map((task) => (
                        <Box
                          key={task.id}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            p: 1.5,
                            border: `1px solid ${neutrals.gray[200]}`,
                            borderRadius: "12px",
                            transition: "background-color 150ms",
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                            cursor: "pointer",
                            position: "relative",
                          }}
                          onClick={() => navigate(clientProgressDetailsRoute)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor:
                                  task.priority === "high"
                                    ? "#dc2626"
                                    : task.priority === "medium"
                                      ? "#d97706"
                                      : "#0369a1",
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="subtitle2"
                              sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}
                            >
                              {task.name}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              "-webkit-line-clamp": 2,
                              "-webkit-box-orient": "vertical",
                              flex: 1,
                              ml: 1.5 + 0.5, // gap + dot width
                            }}
                          >
                            Fase: {task.phase_name}
                          </Typography>
                          <Badge
                            color={
                              task.priority === "high"
                                ? "red"
                                : task.priority === "medium"
                                  ? "yellow"
                                  : "blue"
                            }
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                            }}
                          >
                            {task.priority === "high"
                              ? "Alta"
                              : task.priority === "medium"
                                ? "Media"
                                : "Baja"}
                          </Badge>
                        </Box>
                      ))}
                      {!hasTasksInProgress && (
                        <EmptyStateMessage
                          icon={ClipboardList}
                          message="No hay tareas en progreso"
                          sx={{ py: 4 }}
                        />
                      )}
                    </>
                  )}
                </Box>

                {hasTasksInProgress && tasksInProgress.length > 0 && (
                  <TablePagination
                    component="div"
                    count={tasksInProgress.length}
                    page={tasksPage}
                    onPageChange={handleTasksPageChange}
                    rowsPerPage={tasksPageSize}
                    onRowsPerPageChange={handleTasksRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                    }
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider}`,
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                      "& .MuiTablePagination-toolbar": {
                        px: { xs: 1, md: 2 },
                        flexWrap: { xs: "wrap", md: "nowrap" },
                        gap: { xs: 1, md: 0 },
                        width: "100%",
                        maxWidth: "100%",
                        overflow: "hidden",
                      },
                      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                      {
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                        whiteSpace: { xs: "nowrap", md: "nowrap" },
                      },
                      "& .MuiTablePagination-select": {
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                      },
                    }}
                  />
                )}
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Resumen de fases */}
        {/* <Card>
          <CardHeader>
            <CardTitle>Resumen de Fases del Itinerario</CardTitle>
          </CardHeader>
          <CardContent>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                },
                gap: 2,
              }}
            >
              {clientSetupProgress.phases.map((phase, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor:
                      phase.status === "completed"
                        ? "#C4E8D2"
                        : phase.status === "in-progress"
                        ? "#bfdbfe"
                        : neutrals.gray[200],
                    backgroundColor:
                      phase.status === "completed"
                        ? "#f0fdf4"
                        : phase.status === "in-progress"
                        ? "#eff6ff"
                        : neutrals.gray[50],
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {phase.status === "completed" ? (
                      <CheckCircle size={16} color="#0F9D58" />
                    ) : phase.status === "in-progress" ? (
                      <PlayCircle size={16} color="#2563eb" />
                    ) : (
                      <Clock size={16} color="#6b7280" />
                    )}
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, fontSize: "0.875rem" }}
                    >
                      Fase {index + 1}
                    </Typography>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 500, fontSize: "0.875rem", mb: 1 }}
                  >
                    {phase.name}
                  </Typography>
                  {phase.completedAt && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.75rem",
                        display: "block",
                        mb: 0.5,
                      }}
                    >
                      Completada:{" "}
                      {new Date(phase.completedAt).toLocaleDateString("es-ES", {
                        day: "numeric",
                        month: "short",
                      })}
                    </Typography>
                  )}
                  {phase.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.75rem",
                        display: "block",
                      }}
                    >
                      {phase.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card> */}

        {/* Anuncios: box alargado bajo notificaciones y mensajes recientes */}
        <AnnouncementsPanel
          ref={announcementsPanelRef}
          currentUser={currentUser}
          interceptorReady={interceptorReady}
          dashboardRefreshKey={dashboardRefreshKey}
          defaultPageSize={CLIENT_ANNOUNCEMENTS_PAGE_SIZE}
          maxHeight="none"
        />

        {/* Modales para Cliente */}
        <IncidentFiltersModal
          open={isNotificationFilterModalOpen}
          onClose={handleCloseNotificationFilters}
          title="Filtros avanzados"
          filterConfigs={notificationFilterConfigs}
          draftFilters={draftNotificationFilters}
          onChange={handleDraftNotificationFilterChange}
          onApply={handleApplyNotificationFilters}
          onClearAll={handleClearNotificationDraftFilters}
        />



        <SuccessPopup
          isOpen={successPopup.isOpen}
          message={successPopup.message}
          error={successPopup.error}
          onClose={() =>
            setSuccessPopup((prev) => ({ ...prev, isOpen: false }))
          }
        />
      </Box>
    );
  }

  // Vista para Consultor, Admin y Super Admin
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        pb: { xs: "80px", md: 0 },
      }}
    >
      {/* Stats Generales - Franja ancha */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 1fr",
            lg: "repeat(4, 1fr)",
          },
          gap: 3,
        }}
      >
        {currentUser.role === "superadmin" && !selectedFilter ? (
          <>
            <MetricCard
              icon={Building2}
              iconColor={neutrals.gray[600]}
              iconBackgroundColor={neutrals.gray[100]}
              value={metrics.totalAgencies}
              label="Agencias Totales"
              isLoading={isDashboardDataLoading}
              valueAdornment={<ArchivedCount count={metrics.archivedAgencies} />}
              onClick={() => navigate("/team")}
              sx={{
                cursor: "pointer",
                "&:hover .MuiPaper-root": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transition: "box-shadow 200ms ease",
                },
              }}
            />

            <MetricCard
              icon={UserPlus}
              iconColor={colors.primary.main}
              iconBackgroundColor={`${colors.primary.main}15`}
              value={metrics.newAgencies}
              label="Nuevas Agencias"
              isLoading={isDashboardDataLoading}
              onClick={() => navigate("/team")}
              sx={{
                cursor: "pointer",
                "&:hover .MuiPaper-root": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transition: "box-shadow 200ms ease",
                },
              }}
            />

            <MetricCard
              icon={Users}
              iconColor={neutrals.gray[600]}
              iconBackgroundColor={neutrals.gray[100]}
              value={metrics.totalClients}
              label="Clientes Totales"
              isLoading={isDashboardDataLoading}
              onClick={() => navigate("/clients")}
              sx={{
                cursor: "pointer",
                "&:hover .MuiPaper-root": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transition: "box-shadow 200ms ease",
                },
              }}
            />

            <MetricCard
              icon={TrendingUp}
              iconColor={colors.primary.main}
              iconBackgroundColor={`${colors.primary.main}15`}
              value={metrics.newClients}
              label="Nuevos Clientes"
              isLoading={isDashboardDataLoading}
              onClick={() => navigate("/clients")}
              sx={{
                cursor: "pointer",
                "&:hover .MuiPaper-root": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transition: "box-shadow 200ms ease",
                },
              }}
            />
          </>
        ) : (
          <>
            <MetricCard
              icon={Users}
              iconColor={neutrals.gray[600]}
              iconBackgroundColor={neutrals.gray[100]}
              value={metrics.totalClients}
              label="Clientes Totales"
              isLoading={isDashboardDataLoading}
              onClick={() => navigate("/clients")}
              sx={{
                cursor: "pointer",
                "&:hover .MuiPaper-root": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transition: "box-shadow 200ms ease",
                },
              }}
            />

            <MetricCard
              icon={UserPlus}
              iconColor={colors.primary.main}
              iconBackgroundColor={`${colors.primary.main}15`}
              value={metrics.newClients}
              label="Clientes Nuevos"
              isLoading={isDashboardDataLoading}
              onClick={() => navigate("/clients")}
              sx={{
                cursor: "pointer",
                "&:hover .MuiPaper-root": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transition: "box-shadow 200ms ease",
                },
              }}
            />

            <MetricCard
              icon={Clock}
              iconColor={neutrals.gray[600]}
              iconBackgroundColor={neutrals.gray[100]}
              value={metrics.pendingOnboardings}
              label="Onboardings Pendientes"
              isLoading={isDashboardDataLoading}
              onClick={() => navigate("/clients")}
              sx={{
                cursor: "pointer",
                "&:hover .MuiPaper-root": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transition: "box-shadow 200ms ease",
                },
              }}
            />

            <MetricCard
              icon={Settings}
              iconColor={neutrals.gray[600]}
              iconBackgroundColor={neutrals.gray[100]}
              value={metrics.activeSetups}
              label="Setups Activos"
              isLoading={isDashboardDataLoading}
              onClick={() => navigate("/setup")}
              sx={{
                cursor: "pointer",
                "&:hover .MuiPaper-root": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  transition: "box-shadow 200ms ease",
                },
              }}
            />
          </>
        )}
      </Box>

      {/* Zona central dividida en dos columnas */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 3,
        }}
      >
        {/* Columna izquierda: Panel de Notificaciones */}
        <NotificationPanel
          notifications={notifications}
          isLoading={isNotificationsLoading}
          error={notificationsError}
          unreadCount={unreadNotificationsCount}
          isUnreadCountLoading={isUnreadCountLoading}
          onMarkAllRead={handleMarkAllNotificationsRead}
          onMarkRead={handleMarkNotificationRead}
          onNotificationClick={handleNotificationClick}
          isMarkingAllRead={isMarkingAllRead}
          clearingThreadId={clearingThreadId}
          currentUser={currentUser}
          clientNameById={clientNameById}
          getNotificationSummary={getNotificationSummary}
          getNotificationRoute={getNotificationRoute}
          selectedNotificationFilter={selectedNotificationFilter}
          hasNotifications={hasNotifications}
          showFilters={true}
          onFilterChange={handleNotificationFilterChange}
          filterRightContent={
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <Tooltip title="Editar filtros" arrow>
                <Button
                  variant="outline"
                  size="sm"
                  sx={{
                    borderColor: hasNotificationFiltersApplied
                      ? neutrals.gray[300]
                      : neutrals.gray[200],
                    color: neutrals.gray[600],
                    flexShrink: 0,
                    minWidth: "32px",
                    height: "32px",
                    padding: "4px",
                    backgroundColor: "white",
                  }}
                  aria-label="Abrir filtros avanzados"
                  aria-pressed={hasNotificationFiltersApplied}
                  onClick={handleOpenNotificationFilters}
                >
                  <SlidersHorizontal size={16} />
                </Button>
              </Tooltip>
              {hasNotificationFiltersApplied && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "-7px",
                    right: "-7px",
                    minWidth: "18px",
                    height: "18px",
                    borderRadius: "9px",
                    backgroundColor: neutrals.gray[400],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "white",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  {activeNotificationFiltersCount}
                </Box>
              )}
            </Box>
          }
          showPagination={true}
          page={notificationPage}
          rowsPerPage={notificationPageSize}
          totalCount={notificationsTotalForPagination}
          onPageChange={handleNotificationPageChange}
          onRowsPerPageChange={handleNotificationRowsPerPageChange}
          sx={{
            height: { xs: "auto", lg: "100%" },
            maxHeight: { xs: "none", lg: "calc(100vh - 280px)" },
            overflow: { xs: "visible", lg: "hidden" },
          }}
        />

        {/* Columna derecha: Anuncios y/o Tareas dependiendo del rol */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: { xs: "auto", lg: "100%" },
            minHeight: 0,
            maxHeight: { xs: "none", lg: rightColumnMaxHeight },
            overflow: { xs: "visible", lg: "hidden" },
          }}
        >
          {shouldShowAnnouncementsPanel && (
            <AnnouncementsPanel
              ref={announcementsPanelRef}
              currentUser={currentUser}
              interceptorReady={interceptorReady}
              dashboardRefreshKey={dashboardRefreshKey}
              forceSuperAdminLayout={isAgencyScopedUser}
            />
          )}
        </Box>
      </Box>

      {shouldShowFullWidthTasksPanel && (
        <Box sx={{ mt: 2 }}>
          <MyTasksPanel
            interceptorReady={interceptorReady}
            dashboardRefreshKey={dashboardRefreshKey}
            selectedFilter={selectedFilter}
            selectedFilterData={selectedFilterData}
            filterRefresh={filterRefresh}
            paginationInHeader={true}
            maxHeight="none"
          />
        </Box>
      )}

      <IncidentFiltersModal
        open={isNotificationFilterModalOpen}
        onClose={handleCloseNotificationFilters}
        title="Filtros avanzados"
        filterConfigs={notificationFilterConfigs}
        draftFilters={draftNotificationFilters}
        onChange={handleDraftNotificationFilterChange}
        onApply={handleApplyNotificationFilters}
        onClearAll={handleClearNotificationDraftFilters}
      />

      {/* Task Detail Modal for task_assigned notifications */}
      <TaskDetailModal
        taskId={selectedNotificationTaskId}
        open={Boolean(selectedNotificationTaskId)}
        onClose={() => setSelectedNotificationTaskId(null)}
        interceptorReady={interceptorReady}
        setTasksRefreshKey={setTasksRefreshKey}
        onSuccess={() => { }}
        onError={() => { }}
      />
    </Box>
  );
}
